/**
 * An investigation being composed before the visitor has an account.
 *
 * The whole point of the anonymous flow is that someone can choose a plan, pick
 * their checks and type in who they are checking WITHOUT signing up first —
 * they are only asked to authenticate at the moment of submission. Signing up
 * navigates away (and may detour through email/phone verification), so the
 * in-progress work has to outlive a full page teardown.
 *
 * That is what this is: the flow's state, parked in localStorage at the moment
 * auth is required and picked back up when the visitor lands back in the app.
 * Resume is keyed off the draft existing rather than off a returnTo parameter,
 * because the journey (signup → verify → dashboard) has several exits and a
 * query string does not survive all of them.
 *
 * Short-lived on purpose: a half-finished check from last week is more likely
 * to confuse than to help, so it expires rather than silently reappearing.
 */

const KEY = 'yourchecka.investigationDraft';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

/** One person in the draft. Mirrors BatchSubject, kept loose for storage. */
export interface DraftSubject {
    employeeFirstName: string;
    employeeMiddleName?: string;
    employeeLastName: string;
    employeeEmail: string;
    employeePhone: string;
    employeeGender?: string;
}

export interface InvestigationDraft {
    checkType: string;
    /** Feature ids kept. A short list means they cherry picked. */
    features: string[];
    addons: string[];
    planName: string;
    pricePerSubject: number;
    /** Which arm of the flow they were in when auth interrupted them. */
    mode: 'single' | 'bulk';
    /** Everyone entered so far. A single-mode draft holds exactly one. */
    subjects: DraftSubject[];
    savedAt: number;
}

export function saveDraft(draft: Omit<InvestigationDraft, 'savedAt'>): void {
    try {
        localStorage.setItem(KEY, JSON.stringify({ ...draft, savedAt: Date.now() }));
    } catch {
        // Private browsing, disabled storage, quota. The visitor re-enters their
        // details after signing in, so this must never break the submit button.
    }
}

/** The parked draft, or null if there is none, it is stale, or unreadable. */
export function readDraft(): InvestigationDraft | null {
    try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return null;

        const parsed = JSON.parse(raw) as InvestigationDraft;
        // A draft with no plan or nobody in it cannot be resumed into anything.
        if (typeof parsed?.checkType !== 'string' || !Array.isArray(parsed.subjects)) {
            return null;
        }
        if (parsed.subjects.length === 0) return null;
        if (!parsed.savedAt || Date.now() - parsed.savedAt > MAX_AGE_MS) {
            clearDraft();
            return null;
        }

        return {
            ...parsed,
            features: Array.isArray(parsed.features) ? parsed.features : [],
            addons: Array.isArray(parsed.addons) ? parsed.addons : [],
            mode: parsed.mode === 'bulk' ? 'bulk' : 'single',
        };
    } catch {
        return null;
    }
}

export function clearDraft(): void {
    try {
        localStorage.removeItem(KEY);
    } catch {
        // Nothing to do; a stale entry expires on its own.
    }
}

export function hasDraft(): boolean {
    return readDraft() !== null;
}
